import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import HoverTooltip from '../../../components/ui/HoverTooltip';

const MakeOfferSection = ({
  property,
  onOfferSubmit,
  canSubmitOffer = true,
  isOfferOpen = true,
  isOwnProduct = false,
  ownProductBidTooltip = '',
  onRequireMarketUser,
  currentUserId,
}) => {
  const [offerAmount, setOfferAmount] = useState('');
  const [quantity, setQuantity] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const availableQuantity = Number(property?.availableQuantity ?? property?.quantity ?? 0);
  const isOfferDisabled = !canSubmitOffer || !isOfferOpen || isOwnProduct;

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!isOfferOpen || isOwnProduct) {
      return;
    }

    if (!canSubmitOffer) {
      onRequireMarketUser?.();
      return;
    }

    setIsSubmitting(true);

    try {
      const offerData = {
        productId: property?.id,
        amount: parseFloat(offerAmount),
        quantity: parseInt(quantity, 10),
        message,
        timestamp: new Date()?.toISOString(),
      };

      if (onOfferSubmit) {
        await onOfferSubmit(offerData);
      }

      setOfferAmount('');
      setQuantity('');
      setMessage('');
    } catch (error) {
      console.error('Teklif gönderilirken hata oluştu:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="DollarSign" size={20} className="text-primary" />
        <h3 className="text-lg font-semibold text-text-primary">Teklif Ver</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {!isOfferOpen ? (
          <div className="rounded-md border border-secondary-300 bg-secondary-100 px-3 py-2 text-sm text-text-secondary">
            Bu ürün şu anda teklife açık değil.
          </div>
        ) : null}

        {!canSubmitOffer ? (
          <div className="rounded-md border border-warning bg-warning-100 px-3 py-2 text-sm text-warning">
            Teklif göndermek için pazaryeri hesabı ile giriş yapmalısınız.
          </div>
        ) : null}

        {isOwnProduct ? (
          <div className="rounded-md border border-warning bg-warning-100 px-3 py-2 text-sm text-warning">
            Bu ürün size ait. Kendi ürününüze teklif gönderemezsiniz.
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Teklif Fiyatı (/{property?.priceUnit?.replace('per ', '') || 'birim'})
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-text-secondary">$</span>
            </div>
            <input
              type="number"
              value={offerAmount}
              onChange={(event) => setOfferAmount(event?.target?.value)}
              min="1"
              step="0.01"
              disabled={isOfferDisabled}
              required
              className="block w-full pl-8 pr-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder={`${property?.price || 0}`}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Miktar ({property?.unit || 'birim'})
          </label>
          <input
            type="number"
            value={quantity}
            onChange={(event) => setQuantity(event?.target?.value)}
            min="1"
            max={availableQuantity || 1000}
            disabled={isOfferDisabled}
            required
            className="block w-full px-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
            placeholder={`Maks: ${availableQuantity || 0}`}
          />
          <p className="text-xs text-text-secondary mt-1">
            Kullanılabilir: {availableQuantity || 0} {property?.unit || 'birim'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Mesaj (opsiyonel)
          </label>
          <textarea
            value={message}
            onChange={(event) => setMessage(event?.target?.value)}
            rows={3}
            disabled={isOfferDisabled}
            className="block w-full px-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
            placeholder="Özel talep veya sorularını buraya yazabilirsin..."
          />
        </div>

        {offerAmount && quantity ? (
          <div className="bg-secondary-50 p-3 rounded-md">
            <div className="flex justify-between items-center text-sm">
              <span className="text-text-secondary">Toplam Teklif:</span>
              <span className="font-semibold text-text-primary">
                ${(parseFloat(offerAmount) * parseInt(quantity || 0, 10))?.toFixed(2)}
              </span>
            </div>
          </div>
        ) : null}

        <HoverTooltip
          content={isOwnProduct ? ownProductBidTooltip : undefined}
          className="block w-full"
        >
          <button
            type="submit"
            disabled={isOfferDisabled || isSubmitting || !offerAmount || !quantity}
            className="w-full flex items-center justify-center space-x-2 bg-primary text-white py-3 rounded-md border border-transparent hover:bg-primary-700 disabled:bg-primary disabled:text-white disabled:border-primary disabled:opacity-[0.65] disabled:cursor-not-allowed transition-all duration-200"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Gönderiliyor...</span>
              </>
            ) : (
              <>
                <Icon name="Send" size={16} />
                <span>
                  {!isOfferOpen
                    ? 'Teklife Kapalı'
                    : isOwnProduct
                      ? 'Kendi Ürünün'
                      : canSubmitOffer
                        ? 'Teklif Gönder'
                        : 'Giriş Gerekli'}
                </span>
              </>
            )}
          </button>
        </HoverTooltip>
      </form>

      <div className="mt-4 pt-4 border-t border-border">
        <h4 className="text-sm font-medium text-text-primary mb-2">Son Teklifler</h4>
        <div className="space-y-2">
          {property?.currentBids?.slice(0, 3)?.map((bid) => (
            <div key={bid?.id} className="flex justify-between items-center text-xs">
              <span className="inline-flex items-center gap-1.5 text-text-secondary">
                <Icon
                  name={bid?.buyerId && currentUserId && bid.buyerId === currentUserId ? 'UserCheck' : 'UserRound'}
                  size={12}
                  className={bid?.buyerId && currentUserId && bid.buyerId === currentUserId ? 'text-primary' : 'text-text-secondary'}
                />
                {bid?.buyerId && currentUserId && bid.buyerId === currentUserId ? 'Size Ait' : bid?.bidder}
                {bid?.status === 'ACCEPTED' ? ' • kabul edildi' : ''}
              </span>
              <span className="font-medium text-text-primary">
                ${bid?.amount} × {bid?.acceptedQuantity || bid?.quantity}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MakeOfferSection;
